import { oc } from "@orpc/contract";
import { z } from "zod";
import { listInput, listPage } from "./list";
import { Cents } from "./money";
import { VatBasis } from "./price";
import { ProductKind } from "./product";

/**
 * The shop's catalog (spec, "API": "catalogus (lijst, zoeken, product met varianten,
 * beschikbaarheid en de prijs die voor de klant geldt)"). Public: a guest or a signed-in customer.
 *
 * Only what is for sale appears: active products with at least one active variant, and only those
 * variants. Inactive and deleted products and variants are never in an answer.
 *
 * Prices are the ones for whoever asks: a guest pays Consument prices incl VAT; with a customer
 * session (`__Host-customer-session`) the customer's group and own prices apply, and a business
 * group's prices are excl VAT. `vatBasis` says which side of VAT `amount` is on; `amountExcl` and
 * `amountIncl` give both. Answers that depend on a session are sent `Cache-Control: private`.
 */

// ---------------------------------------------------------------------------------------------
// Fields

/**
 * A product photo as the shop shows it: the original and the sizes made from it (smallest first,
 * at least 400, 800 and 1600 pixels wide). URLs may be root-relative.
 */
export const ShopPhoto = z.object({
	id: z.string(),
	/** The photo's `alt`: its alt text as written in the admin, or the product's name without one. */
	alt: z.string(),
	url: z.string(),
	sizes: z.array(z.object({ width: z.number().int(), url: z.string() })),
});
export type ShopPhoto = z.infer<typeof ShopPhoto>;

/** What one unit costs the buyer. */
export const ShopPrice = z.object({
	/** The side of VAT `amount` is on: Consument incl VAT, business groups excl VAT. */
	vatBasis: VatBasis,
	/** The unit price to show, on `vatBasis`. */
	amount: Cents,
	amountExcl: Cents,
	amountIncl: Cents,
	/** The product's VAT rate, an integer percentage. */
	vatRate: z.number().int(),
});
export type ShopPrice = z.infer<typeof ShopPrice>;

/** A product's price in a list: the lowest of its variants, and whether the others differ ("vanaf"). */
export const ShopFromPrice = ShopPrice.extend({
	/** True when the product's variants do not all cost the same: show "vanaf". */
	from: z.boolean(),
});
export type ShopFromPrice = z.infer<typeof ShopFromPrice>;

/**
 * `pricePerPiece` in a list or on a product page (#161): the lowest price per piece over the
 * variants that have pieces, on the same side of VAT as `price`, and whether they differ ("vanaf").
 */
export const ShopFromPricePerPiece = z.object({
	/** In cents, rounded half up: "€0,35 per doek". */
	amount: Cents,
	/** What a piece is called for the variant with the lowest price per piece ("doek"); `null`: not said. */
	pieceNoun: z.string().nullable(),
	/** True when the variants with pieces do not all cost the same per piece: show "vanaf". */
	from: z.boolean(),
});
export type ShopFromPricePerPiece = z.infer<typeof ShopFromPricePerPiece>;

/** "From `minQuantity` pieces, `percent` off": the unit price at that step, for this buyer. */
export const ShopQuantityPrice = z.object({
	minQuantity: z.number().int(),
	percent: z.number().int(),
	price: ShopPrice,
});
export type ShopQuantityPrice = z.infer<typeof ShopQuantityPrice>;

/**
 * A group (`category`): a division of the assortment with its own page in the shop
 * (`/producten/{slug}`). Groups come in `position` order, lowest first.
 */
export const ShopCategory = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	position: z.number().int(),
	/** The short text on the group's page; can be empty. */
	description: z.string(),
	/** For the page's `<title>`: the group's SEO title, or its name when that is empty. */
	seoTitle: z.string(),
	/** For the meta description: the group's SEO description, or else the start of its text; can be empty. */
	seoDescription: z.string(),
});
export type ShopCategory = z.infer<typeof ShopCategory>;

/**
 * A pack (#259): one variant with pieces per pack, of a product in a line (`productLine`) or of a
 * product without a line whose variants all have pieces. Priced for the buyer, like the rest.
 */
export const ShopPack = z.object({
	productId: z.string(),
	/** The product's slug: its own page (`/product/{slug}`). */
	slug: z.string(),
	/** The product's name. */
	name: z.string(),
	variantId: z.string(),
	piecesPerPack: z.number().int(),
	/** What one piece is called ("doek", "navulling"); `null`: not said ("stuk"). */
	pieceNoun: z.string().nullable(),
	/** The price of one pack. */
	price: ShopPrice,
	/** `price.amount` divided by `piecesPerPack`, in cents rounded half up. */
	pricePerPiece: Cents,
	/** Whether it can be ordered now. */
	inStock: z.boolean(),
	/**
	 * The smallest pack: the fewest pieces per pack, also when it is out of stock. Exactly one pack
	 * of a set is; it is the one "Bespaar X%" (`savingPercent`) compares with.
	 */
	smallest: z.boolean(),
});
export type ShopPack = z.infer<typeof ShopPack>;

/**
 * "Bespaar X%" (#259): (the smallest pack's price per piece − this pack's) ÷ the smallest pack's,
 * rounded to whole percents, half up; `null` unless that is above zero. Only ever against the
 * smallest pack of the same line (or product), never another brand. The API and the shop both use it.
 */
export const savingPercent = (smallestPricePerPiece: number, pricePerPiece: number): number | null => {
	if (smallestPricePerPiece <= 0) return null;
	const percent = Math.round(((smallestPricePerPiece - pricePerPiece) * 100) / smallestPricePerPiece);
	return percent > 0 ? percent : null;
};

/**
 * A product in the list against the smallest pack of its line (or of its own variants): for "Bespaar
 * X%" and "Kleinste pak" on the tile without asking for the packs.
 */
export const ShopPackSaving = z.object({
	/** The product's lowest price per piece (`ShopProductSummary.pricePerPiece`) is the smallest pack's. */
	smallest: z.boolean(),
	/** The smallest pack's price per piece, on the same side of VAT as the product's. */
	smallestPricePerPiece: Cents,
	/** `savingPercent(smallestPricePerPiece, pricePerPiece.amount)`. */
	percent: z.number().int().nullable(),
});
export type ShopPackSaving = z.infer<typeof ShopPackSaving>;

// ---------------------------------------------------------------------------------------------
// Reviews (#266): read in the shop

/**
 * What buyers think of a product (`productReview`), over its published reviews: approved by an
 * employee (`pending` and `rejected` are never public). On a product page it counts the reviews of
 * every product in its line (`productLine`), or of only the product without a line; for the home,
 * the whole shop's.
 */
export const ShopReviewSummary = z.object({
	/** The average stars, rounded to one decimal, half up (4,6); `null` without reviews. */
	average: z.number().nullable(),
	/** How many reviews; 0 without any (the shop shows "Nog geen reviews"). */
	count: z.number().int(),
	/** How many reviews gave each number of stars: always five entries, 5 stars first. */
	distribution: z.array(z.object({ stars: z.number().int(), count: z.number().int() })),
});
export type ShopReviewSummary = z.infer<typeof ShopReviewSummary>;

/**
 * One published review as the shop shows it ("Reviewkaart"): always "Geverifieerde koper", since a
 * review is written only through the link in the invitation after an order.
 */
export const ShopReview = z.object({
	id: z.string(),
	/** 1 to 5. */
	stars: z.number().int(),
	/** `null`: none. */
	title: z.string().nullable(),
	/** `null`: none. */
	body: z.string().nullable(),
	/** As the buyer wanted it shown; `""` without one or after anonymising: the shop shows "Een koper". */
	firstName: z.string(),
	/** The pack reviewed ("Pak van 100"): the product, and the pieces per pack of the variant bought. */
	pack: z.object({
		productId: z.string(),
		/** The product's name. */
		name: z.string(),
		/** As `ShopVariant.piecesPerPack`; `null` for a product without pieces. */
		piecesPerPack: z.number().int().nullable(),
		pieceNoun: z.string().nullable(),
	}),
	/** When the buyer wrote it (ISO 8601). */
	writtenAt: z.iso.datetime(),
	/** Every published review hangs on an order: "Geverifieerde koper". */
	verifiedBuyer: z.literal(true),
});
export type ShopReview = z.infer<typeof ShopReview>;

/** A product page's reviews come in pages of this many. */
export const SHOP_REVIEW_PAGE_SIZE = 10;

/** A page of a product's reviews: the line's, newest first. */
export const ShopReviewListPage = listPage(ShopReview);
export type ShopReviewListPage = z.infer<typeof ShopReviewListPage>;

/** For the home ("Waarom je hier koopt"): the newest reviews (never the best), and the shop's summary. */
export const ShopRecentReviews = z.object({
	/** The three newest published reviews, newest first; fewer when there are fewer. */
	reviews: z.array(ShopReview),
	/** Over every published review in the shop. */
	summary: ShopReviewSummary,
});
export type ShopRecentReviews = z.infer<typeof ShopRecentReviews>;

/** A fit (`fit`): the variant "past op" (`fits`) the holder, or is "niet voor" it (`notFor`). */
export const ShopFit = z.enum(["fits", "notFor"]);
export type ShopFit = z.infer<typeof ShopFit>;

// ---------------------------------------------------------------------------------------------
// The list

/** A product in the shop's list. */
export const ShopProductSummary = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	kind: ProductKind,
	/** The first photo, or `null` without photos. */
	photo: ShopPhoto.nullable(),
	price: ShopFromPrice,
	/** The lowest price per piece; `null` when no variant has pieces. */
	pricePerPiece: ShopFromPricePerPiece.nullable(),
	/** At least one variant can be ordered now. */
	inStock: z.boolean(),
	/**
	 * The size label (`sizeLabel`) to show with the product: that of the variant whose price `price`
	 * shows (the cheapest), so the two belong together. `null` when that variant has no size or fit.
	 */
	sizeLabel: z.string().nullable(),
	/** The line (`productLine`) it is a pack of; `null` in none. Named in `ShopProductListPage.lines`. */
	lineId: z.string().nullable(),
	/** Against the smallest pack; `null` for a product that has no packs (`ShopProduct.packs`) or no pieces. */
	saving: ShopPackSaving.nullable(),
	/**
	 * The variant "In mand" on its tile puts in the cart: the product's only variant for sale (a
	 * wiper, a handle, a starter kit). `null` with more than one, where the buyer chooses on the
	 * product page (#301).
	 */
	variantId: z.string().nullable(),
});
export type ShopProductSummary = z.infer<typeof ShopProductSummary>;

/**
 * Ids in a query string: repeated (`?value=a&value=b`), in bracket notation (`?value[0]=a`, as the
 * oRPC client sends an array) or comma-separated (`?value=a,b`). A bad value is no filter.
 */
const IdList = z
	.union([z.string(), z.array(z.string())])
	.transform((ids) =>
		(Array.isArray(ids) ? ids : ids.split(","))
			.map((id) => id.trim())
			.filter((id) => id !== "")
			.slice(0, 50),
	)
	.optional()
	.catch(undefined);

/**
 * The shop's product list: searched on name, description, SKU and EAN; filtered on kind, on being
 * in stock, on a group, on attribute values and on "past op"; sorted by name, by price (the buyer's
 * lowest), by when it was added (`sort=createdAt&order=desc`: newest first), by price per piece
 * (`pricePerPiece`: the buyer's lowest per piece first, products without pieces after them on price)
 * or by popularity (`popular`: the most sold in paid and on-account orders of the last 90 days first,
 * a tie on name; `order=desc` gives the fewest first). Whatever the sort and the order, what is not
 * in stock comes after what is (#263).
 *
 * Attribute values combine as the shop's filters do: values of one attribute are alternatives
 * (46×27 cm or 40×40 cm), different attributes all apply (46×27 cm and wasbaar). Several holders
 * in `fits` are alternatives too. Values, "past op" and `inStock` must all hold for one and the
 * same variant for sale (`ShopVariant.attributeValueIds`: a value set on the product holds for its
 * variants). Value and holder ids the catalog does not know are ignored, as any bad value in a URL;
 * a group slug it does not know holds nothing.
 */
export const ShopProductListInput = listInput({
	sort: ["name", "price", "createdAt", "pricePerPiece", "popular"],
	defaultSort: "name",
	filters: {
		kind: ProductKind.optional().catch(undefined),
		/** `yes`: only products with at least one variant in stock. */
		inStock: z.enum(["yes"]).optional().catch(undefined),
		/** A group's slug: only the products in that group. */
		category: z.string().trim().min(1).max(200).optional().catch(undefined),
		/** Attribute value ids (`ShopProductListPage.attributes`). */
		value: IdList,
		/** Holder ids: only products with a variant that "past op" one of them (`ShopProductListPage.fits`). */
		fits: IdList,
	},
});
export type ShopProductListInput = z.input<typeof ShopProductListInput>;
/** The list query with its defaults filled in, as the API receives it. */
export type ShopProductListQuery = z.output<typeof ShopProductListInput>;

/**
 * The filters with their counts: how many products the list would hold with that one choice
 * changed, every other filter (search, kind, in stock, the other attributes) kept. For a value of
 * an attribute, the attribute's own selection is left out: the count is what choosing that value
 * gives, alone or next to the values already chosen.
 */
export const ShopCategoryCount = ShopCategory.extend({ count: z.number().int() });
export type ShopCategoryCount = z.infer<typeof ShopCategoryCount>;

/** An attribute with every value, in their set order, each with its count (zero included). */
export const ShopAttributeCount = z.object({
	id: z.string(),
	name: z.string(),
	/** The size attribute (formaat): its values are the sizes the size label shows. At most one is. */
	isSize: z.boolean(),
	values: z.array(z.object({ id: z.string(), name: z.string(), count: z.number().int() })),
});
export type ShopAttributeCount = z.infer<typeof ShopAttributeCount>;

/** A holder for the "past op" filter: how many products have a variant that fits it. */
export const ShopHolderCount = z.object({
	id: z.string(),
	name: z.string(),
	/** One of them, for "je XXL-wisser" (#195); `null`: not filled in, say it with `name`. */
	singularName: z.string().nullable(),
	count: z.number().int(),
	/**
	 * The sizes of the variants for sale that "past op" it (`46×27 cm`), each once, in the size
	 * attribute's set order; empty when none has a size (#212). Among the products the search and
	 * kind leave, whatever the other filters (group, values, "past op", in stock) choose.
	 */
	sizes: z.array(z.string()),
});
export type ShopHolderCount = z.infer<typeof ShopHolderCount>;

/**
 * A line in the list (#259), for the wiper chooser, the fit guide, the menu and the saving block.
 * Whatever the list's search, filters and page: a line is the same on every page.
 */
export const ShopLine = z.object({
	id: z.string(),
	name: z.string(),
	/**
	 * "vanaf €X per doek": the lowest price per piece over its packs in stock, or over all its packs
	 * when none is; `from` when those differ.
	 */
	pricePerPiece: ShopFromPricePerPiece,
	/** At least one pack can be ordered now. */
	inStock: z.boolean(),
	/** Its packs, fewest pieces first (then by slug): the first is the smallest, the last the largest. */
	packs: z.array(ShopPack),
});
export type ShopLine = z.infer<typeof ShopLine>;

export const ShopProductListPage = listPage(ShopProductSummary).extend({
	/** Every group in `position` order, with its count. */
	categories: z.array(ShopCategoryCount),
	/** Every attribute in `position` order, with its values' counts. */
	attributes: z.array(ShopAttributeCount),
	/** Every holder in `position` order, with its "past op" count. */
	fits: z.array(ShopHolderCount),
	/** Every line with at least one pack for sale, by name. */
	lines: z.array(ShopLine),
});
export type ShopProductListPage = z.infer<typeof ShopProductListPage>;

// ---------------------------------------------------------------------------------------------
// The detail

/** An option with the values the product's active variants hold, in their set order. */
export const ShopOption = z.object({
	id: z.string(),
	name: z.string(),
	values: z.array(z.object({ id: z.string(), name: z.string() })),
});
export type ShopOption = z.infer<typeof ShopOption>;

/** An attribute with the values its variants for sale hold (`ShopVariant.attributeValueIds`), in set order. */
export const ShopAttribute = z.object({
	id: z.string(),
	name: z.string(),
	values: z.array(z.object({ id: z.string(), name: z.string() })),
});
export type ShopAttribute = z.infer<typeof ShopAttribute>;

/** A stated fit of a variant on a holder. A holder that is not listed is unknown, never "past op". */
export const ShopVariantFit = z.object({
	holderId: z.string(),
	/** As the size label says it: "bekende XXL-wissers". */
	name: z.string(),
	/** One of them, for "Dit past niet op je XXL-wisser" (#195); `null`: not filled in. */
	singularName: z.string().nullable(),
	fit: ShopFit,
});
export type ShopVariantFit = z.infer<typeof ShopVariantFit>;

/** A variant for sale. */
export const ShopVariant = z.object({
	id: z.string(),
	/** The value names in option order (`XXL / Blauw`); the product's name without options. */
	name: z.string(),
	sku: z.string(),
	ean: z.string().nullable(),
	/** One value per option of the product. */
	optionValues: z.array(z.object({ optionId: z.string(), optionValueId: z.string() })),
	/** The price of one piece. */
	price: ShopPrice,
	/**
	 * With the customer's own price for this variant (`customerPrice`, which `price` is): what their
	 * group pays without it, on the same side of VAT ("Lijstprijs" beside "Zakelijk · jouw prijs").
	 * `null` without an own price.
	 */
	listPrice: ShopPrice.nullable(),
	/**
	 * How many pieces (doekjes, navullingen) one pack holds; `null` for a product without pieces. A
	 * bundle without its own number has its components' summed (quantity × pieces).
	 */
	piecesPerPack: z.number().int().nullable(),
	/**
	 * What one piece is called, singular ("doek", "navulling"); `null`: not said. A bundle without its
	 * own takes its components' when they all agree.
	 */
	pieceNoun: z.string().nullable(),
	/**
	 * `price.amount` divided by `piecesPerPack`, in cents rounded half up; `null` without pieces. Not
	 * an order line's unit price, which is per pack.
	 */
	pricePerPiece: Cents.nullable(),
	/** The quantity discount steps that apply to this buyer, ascending; empty without any. */
	quantityPrices: z.array(ShopQuantityPrice),
	/**
	 * Whether it can be ordered now. The shop shows yes or no, never a count; the cart and checkout
	 * check the exact number themselves.
	 */
	inStock: z.boolean(),
	/**
	 * The attribute values that hold for it, in attribute and value order: its product's, except
	 * that where it holds values of its own for an attribute, those replace the product's.
	 */
	attributeValueIds: z.array(z.string()),
	/** Its size: its value of the size attribute (formaat), from `attributeValueIds`; `null` without one. */
	size: z.string().nullable(),
	/** Its stated fits, in holder order: "past op" and "niet voor". */
	fits: z.array(ShopVariantFit),
	/** "46×27 cm · past op bekende XXL-wissers": the size with the fits; `null` with neither. */
	sizeLabel: z.string().nullable(),
});
export type ShopVariant = z.infer<typeof ShopVariant>;

/**
 * One variant in a bundle, for "wat zit erin" (#161): its name and how many one bundle holds, never
 * its price.
 */
export const ShopBundleComponent = z.object({
	variantId: z.string(),
	/** The product's name, with the variant's value names after a dash: "Vloerwisser – XXL". */
	name: z.string(),
	/** Per bundle. */
	quantity: z.number().int(),
	/** The component's own pieces per pack; `null` without pieces. */
	piecesPerPack: z.number().int().nullable(),
	pieceNoun: z.string().nullable(),
});
export type ShopBundleComponent = z.infer<typeof ShopBundleComponent>;

/** A pack on the product page: whether it is this product's (`ShopPack`). */
export const ShopProductPack = ShopPack.extend({
	/**
	 * A pack of this very product. A product whose variants are its packs has all of them: which one
	 * is chosen is the variant in the URL.
	 */
	current: z.boolean(),
});
export type ShopProductPack = z.infer<typeof ShopProductPack>;

/** One product as the shop's product page shows it. */
export const ShopProduct = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	kind: ProductKind,
	/** Plain text as Richard typed it. */
	description: z.string(),
	/** For the page's `<title>`: the product's SEO title, or its name when that is empty. */
	seoTitle: z.string(),
	/**
	 * For the page's meta description: the product's SEO description, or else the start of its
	 * description (at most `SEO_DESCRIPTION_FALLBACK_LENGTH` characters, cut at a word); can be empty.
	 */
	seoDescription: z.string(),
	photos: z.array(ShopPhoto),
	options: z.array(ShopOption),
	/** By the first option's value order, then the second's, and so on (as the admin's grid). */
	variants: z.array(ShopVariant),
	price: ShopFromPrice,
	/** The lowest price per piece; `null` when no variant has pieces. */
	pricePerPiece: ShopFromPricePerPiece.nullable(),
	/** A bundle's components in their set order; empty for a standard product. */
	components: z.array(ShopBundleComponent),
	/** At least one variant can be ordered now. */
	inStock: z.boolean(),
	/** The groups the product is in, in `position` order. */
	categories: z.array(ShopCategory),
	/** The attributes with the values its variants hold, in `position` order. */
	attributes: z.array(ShopAttribute),
	/** The size label under the photos: as `ShopProductSummary.sizeLabel`. Each variant has its own. */
	sizeLabel: z.string().nullable(),
	/** The line (`productLine`) it is a pack of; `null` in none. */
	lineId: z.string().nullable(),
	/**
	 * The packs to choose between (#259), fewest pieces first (then by slug), each priced for the
	 * buyer. In a line: every pack of the line for sale, out of stock ones too. Without a line: its own
	 * variants when every one has pieces per pack. Otherwise empty.
	 */
	packs: z.array(ShopProductPack),
	/** Over the published reviews of every product in its line, or of only this one without a line (#266). */
	reviewSummary: ShopReviewSummary,
});
export type ShopProduct = z.infer<typeof ShopProduct>;

// ---------------------------------------------------------------------------------------------
// Holders: the wisserpagina and the fit guide (#264)

/** A holder named in a fit: "past op Swiffer XXL", "niet voor de standaard vloerwisser". */
export const ShopFitHolder = z.object({
	id: z.string(),
	/** As the size label says it: "standaard wissers". */
	name: z.string(),
	/** One of them ("de standaard vloerwisser"); `null`: not filled in, say it with `name`. */
	singularName: z.string().nullable(),
});
export type ShopFitHolder = z.infer<typeof ShopFitHolder>;

/**
 * A holder with its fit, for a wisserpagina ("Wat past") and the fit guide. Its lines are the lines
 * (`ShopProductListPage.lines`) with a pack that "past op" it; its size and fit both ways are what
 * those lines' packs for sale state. Only stated fits are here: a holder in neither list is unknown
 * ("Past op: nog niet bevestigd"), and a holder stated both ways by the packs is in neither.
 */
export const ShopHolder = ShopFitHolder.extend({
	/** Voetmaat, as typed ("42×12 cm"); `null`: not filled in, the shop leaves it out. */
	footSize: z.string().nullable(),
	/** Meettip; `null`: none, the shop leaves it out. */
	measuringTip: z.string().nullable(),
	/** The sizes of its lines' packs that "past op" it (`46×27 cm`), each once, in the size attribute's order. */
	sizes: z.array(z.string()),
	/**
	 * The other holders its lines "past op" (the brands: "Swiffer XXL"), in holder order. Itself is
	 * not in it: that it fits its own holder goes without saying.
	 */
	fits: z.array(ShopFitHolder),
	/** The holders its lines are "niet voor", in holder order: "Niet voor de standaard vloerwisser". */
	notFor: z.array(ShopFitHolder),
	/** Its lines, by name; empty when no line fits it. */
	lineIds: z.array(z.string()),
	/**
	 * "vanaf €X per doek": the lowest price per piece over its lines' packs in stock, or over all of
	 * them when none is (as `ShopLine.pricePerPiece`); `null` without a line.
	 */
	pricePerPiece: ShopFromPricePerPiece.nullable(),
});
export type ShopHolder = z.infer<typeof ShopHolder>;

/**
 * A category's page (`/producten/{slug}`): the category, and its holder when it has one. A category
 * with a holder is a wisserpagina; without one, a category page with filters.
 */
export const ShopCategoryPage = ShopCategory.extend({ holder: ShopHolder.nullable() });
export type ShopCategoryPage = z.infer<typeof ShopCategoryPage>;

/** A holder in the fit guide and on the home, with the category whose wisserpagina it is. */
export const ShopHolderEntry = ShopHolder.extend({
	/** Its category: the first in `position` order when it is the holder of several. */
	category: z.object({ id: z.string(), name: z.string(), slug: z.string() }),
});
export type ShopHolderEntry = z.infer<typeof ShopHolderEntry>;

/** Every holder that is a category's holder, once, in the order of their categories (Duster, Vloerwisser, XXL). */
export const ShopHolderList = z.array(ShopHolderEntry);
export type ShopHolderList = z.infer<typeof ShopHolderList>;

/** The longest description fallback for `ShopProduct.seoDescription`: what search engines show. */
export const SEO_DESCRIPTION_FALLBACK_LENGTH = 160;

const SlugPath = z.object({ slug: z.string().min(1) });

/** `GET /shop/catalog/products/{slug}/reviews?page=`; a bad page is page 1. */
export const ShopReviewListInput = SlugPath.extend({
	page: z.coerce.number().int().min(1).max(100_000).default(1).catch(1),
});
export type ShopReviewListInput = z.input<typeof ShopReviewListInput>;
/** The page's query alone, as the API reads it next to the slug in the path. */
export const ShopReviewListQuery = ShopReviewListInput.omit({ slug: true });
export type ShopReviewListQuery = z.output<typeof ShopReviewListQuery>;

// ---------------------------------------------------------------------------------------------
// Procedures (public: a guest, or a signed-in customer for their own prices)

export const ShopCatalogContract = {
	listProducts: oc
		.route({ method: "GET", path: "/shop/catalog/products" })
		.input(ShopProductListInput)
		.output(ShopProductListPage),
	/** Errors: `CatalogProductNotFound` (404): no such slug, or the product is not for sale. */
	getProduct: oc
		.route({ method: "GET", path: "/shop/catalog/products/{slug}" })
		.input(SlugPath)
		.output(ShopProduct),
	/** Errors: `CatalogCategoryNotFound` (404): no category with this slug. */
	getCategory: oc
		.route({ method: "GET", path: "/shop/catalog/categories/{slug}" })
		.input(SlugPath)
		.output(ShopCategoryPage),
	listHolders: oc.route({ method: "GET", path: "/shop/catalog/holders" }).output(ShopHolderList),
	/**
	 * A product's published reviews (#266): those of every product in its line, newest first, in pages
	 * of `SHOP_REVIEW_PAGE_SIZE`. Errors: `CatalogProductNotFound` (404), as `getProduct`.
	 */
	listReviews: oc
		.route({ method: "GET", path: "/shop/catalog/products/{slug}/reviews" })
		.input(ShopReviewListInput)
		.output(ShopReviewListPage),
	/** The home's three newest published reviews and the shop's summary (#266). */
	recentReviews: oc.route({ method: "GET", path: "/shop/reviews/recent" }).output(ShopRecentReviews),
};
